import { StartupStateScreen } from "@/src/components/StartupStateScreen";
import { WebStartupGate } from "@/src/components/WebStartupGate";
import { useStartup } from "@/src/hooks/useStartup";
import { usePushNotifications } from "@/src/hooks/usePushNotifications";
import { useUnreadCount } from "@/src/hooks/useUnreadCount";
import { postPublishQueue } from "@/src/lib/postPublishQueue";
import { consumeNativeComposerReturn } from "@/src/lib/nativeComposerResume";
import { brand, border, surface, text, typography } from "@/src/theme/tokens";
import { Ionicons } from "@/src/components/ui/ionicons";
import { Tabs, router, usePathname, type Href } from "expo-router";
import React, { useEffect, useRef, useState } from "react";
import { Platform, Text } from "react-native";
export default function TabsLayout() {
  const { user, status, retry } = useStartup();
  const pathname = usePathname();

  const unread = useUnreadCount();
  usePushNotifications(status === "ready" ? user?.uid : null);
  const [composerResumeChecked, setComposerResumeChecked] = useState(Platform.OS !== "android");
  const composerResumeUidRef = useRef<string | null>(null);

  useEffect(() => {
    postPublishQueue.resume();
  }, []);

  useEffect(() => {
    if (Platform.OS !== "android" || status !== "ready") return;
    if (!user?.uid) {
      setComposerResumeChecked(true);
      return;
    }
    if (composerResumeUidRef.current === user.uid) return;

    composerResumeUidRef.current = user.uid;
    setComposerResumeChecked(false);
    void consumeNativeComposerReturn(user.uid)
      .then((returnKind) => {
        if (returnKind) {
          router.replace({
            pathname: "/(tabs)/create",
            params: { resumeExternalActivity: returnKind },
          });
        }
      })
      .catch(() => {})
      .finally(() => setComposerResumeChecked(true));
  }, [status, user?.uid]);

  const hideTabBar = pathname.startsWith("/messages/");

  useEffect(() => {
    if (Platform.OS === "web") return;
    if (status === "signed_out") router.replace("/(auth)/login");
    if (status === "email_verification_required") router.replace("/(auth)/verify-email" as Href);
    if (status === "onboarding_required") router.replace("/(auth)/onboarding");
  }, [status]);

  if (Platform.OS !== "web" && (status === "loading" || status === "signed_out" || status === "email_verification_required" || status === "onboarding_required")) {
    return <StartupStateScreen mode="loading" />;
  }
  if (Platform.OS !== "web" && status === "recoverable_error") {
    return <StartupStateScreen mode="error" offline={false} onRetry={retry} />;
  }
  if (Platform.OS === "android" && status === "ready" && !composerResumeChecked) {
    return <StartupStateScreen mode="loading" />;
  }

  return (
  <Tabs
    backBehavior={Platform.OS === "web" ? "fullHistory" : undefined}
    screenLayout={({ children }) => <WebStartupGate>{children}</WebStartupGate>}
    screenOptions={{
      headerShown: false,
      headerStyle: { backgroundColor: surface.primary },
      headerTintColor: text.primary,
      tabBarStyle: hideTabBar || (Platform.OS === "web" && status !== "ready")
        ? { display: "none" }
        : { backgroundColor: surface.secondary, borderTopColor: border.subtle, elevation: 0, shadowOpacity: 0 },
      tabBarActiveTintColor: brand.primary,
      tabBarInactiveTintColor: text.secondary,
      tabBarLabelStyle: { fontFamily: typography.family.medium, fontSize: 11 },
      tabBarLabel: ({ children, color, focused }) => (
        <Text numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.8}
          style={{ color: focused ? text.brand : color, fontSize: 10, textAlign: "center", fontFamily: focused ? typography.family.semibold : typography.family.medium }}>
          {children}
        </Text>
      ),
    }}
  >
    <Tabs.Screen
      name="index"
      options={{
        title: "Accueil",
        href: Platform.OS === "web" ? "/(tabs)" : undefined,
        tabBarIcon: ({ color, size, focused }) => <Ionicons name={focused ? "home" : "home-outline"} size={size} color={color} />,
      }}
    />
    <Tabs.Screen
      name="messages"
      options={{
        title: "Messages",
        href: Platform.OS === "web" ? "/(tabs)/messages" : undefined,
        tabBarIcon: ({ color, size, focused }) => <Ionicons name={focused ? "chatbubble-ellipses" : "chatbubble-ellipses-outline"} size={size} color={color} />,
        tabBarBadge: unread > 0 ? unread : undefined,
        tabBarBadgeStyle: { backgroundColor: brand.primary, color: text.inverse, fontFamily: typography.family.semibold },
      }}
    />
    <Tabs.Screen
      name="notifications"
      options={{
        href: null,
      }}
    />
    <Tabs.Screen
      name="create"
      options={{
        title: "Créer",
        href: Platform.OS === "web" ? "/(tabs)/create" : undefined,
        tabBarIcon: ({ color, size, focused }) => <Ionicons name={focused ? "add-circle" : "add-circle-outline"} size={size} color={color} />,
      }}
    />

    
    <Tabs.Screen
      name="search"
      options={{
        title: "Recherche",
        href: Platform.OS === "web" ? "/(tabs)/search" : undefined,
        tabBarIcon: ({ color, size }) => <Ionicons name="search" size={size} color={color} />,
  }}
/>
    <Tabs.Screen
      name="profile"
      options={{
        title: "Profil",
        href: Platform.OS === "web" ? "/(tabs)/profile" : undefined,
        tabBarIcon: ({ color, size, focused }) => <Ionicons name={focused ? "person" : "person-outline"} size={size} color={color} />,
      }}
    />
    

  </Tabs>
);
}
